import React, { useState, useEffect, useCallback } from 'react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaPlusCircle,
  FaCamera,
  FaShieldAlt,
  FaIdCard,
  FaCreditCard,
  FaFileInvoice,
  FaCertificate,
  FaBuilding,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
  FaSync,
  FaInfoCircle,
  FaUtensils,
  FaEye,
  FaFileAlt
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { getData, postData, putData } from '../../../Services/Fetchnodeservices';

// 🔥 Define serverURL and IMAGE_BASE_URL
const serverURL = 'https://new.zaptodoor.com';
const IMAGE_BASE_URL = `${serverURL}/uploads/`;

const VerificationScreen = ({ isDark = false }) => {
  const navigate = useNavigate();
  const { restaurantId: paramId } = useParams();
  
  // State
  const [restaurantId, setRestaurantId] = useState(paramId || null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [editMode, setEditMode] = useState(!!paramId);
  const [userData, setUserData] = useState(null);

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    shadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(249, 115, 22, 0.08)',
    successBg: isDark ? 'rgba(22, 163, 74, 0.15)' : 'rgba(22, 163, 74, 0.1)',
    successBorder: isDark ? 'rgba(22, 163, 74, 0.3)' : 'rgba(22, 163, 74, 0.2)',
    warningBg: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
    warningBorder: isDark ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
  };

  // Personal info state
  const [personalInfo, setPersonalInfo] = useState({ 
    status: 'pending', 
    remarks: '',
    ownerName: '',
    restaurantName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    postal: ''
  });

  // Documents state
  const [docs, setDocs] = useState({
    aadhaar: {
      name: 'Aadhaar Card',
      icon: <FaIdCard />,
      status: 'pending',
      number: '',
      frontImage: null,
      backImage: null,
      required: true,
      remarks: ''
    },
    pan: {
      name: 'PAN Card',
      icon: <FaCreditCard />,
      status: 'pending',
      number: '',
      image: null,
      required: true,
      remarks: ''
    },
    bank: {
      name: 'Bank Account',
      icon: <FaBuilding />,
      status: 'pending',
      accountNumber: '',
      ifsc: '',
      bankName: '',
      accountHolder: '',
      passbookImage: null,
      cancelChequeImage: null,
      required: true,
      remarks: ''
    },
    gst: {
      name: 'GST Certificate',
      icon: <FaFileInvoice />,
      status: 'pending',
      number: '',
      image: null,
      required: false,
      remarks: ''
    },
    fssai: {
      name: 'FSSAI License',
      icon: <FaCertificate />,
      status: 'pending',
      number: '',
      image: null,
      required: true,
      remarks: ''
    }
  });

  // 🔥 FIX: Function to get correct image URL using IMAGE_BASE_URL
  const getImageUrl = useCallback((filename) => {
    if (!filename) return null;
    // If it's already a full URL, return as is
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    // Remove any leading slashes
    const cleanFilename = filename.replace(/^\/+/, '');
    // Use IMAGE_BASE_URL
    return `${IMAGE_BASE_URL}${cleanFilename}`;
  }, []);

  const loadDataFromStorage = useCallback(() => {
    try {
      const userDataString = localStorage.getItem('userData');
      const userString = localStorage.getItem('user');
      
      let user = null;
      if (userDataString) {
        user = JSON.parse(userDataString);
      } else if (userString) {
        user = JSON.parse(userString);
      }

      if (user) {
        setUserData(user);
        const id = user.id || user.restaurantId;
        if (id) setRestaurantId(id);
        return user;
      }
      return null;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }, []);

  const parseDocVerification = (user) => {
    const docStatusMap = { 0: 'pending', 1: 'verified', 2: 'rejected' };
    let docVer = {};
    try {
      docVer = user?.doc_verification || {};
      if (typeof docVer === 'string') docVer = JSON.parse(docVer);
    } catch (e) {
      docVer = {};
    }

    const statusMap = (statusCode) => docStatusMap[statusCode] || 'pending';
    const isBankVerified = user?.is_verify_bankinfo === 1 || user?.is_verify_bankinfo === '1';
    const isPersonalVerified = user?.is_verify_personalinfo === 1 || user?.is_verify_personalinfo === '1';

    return {
      aadhaar: { status: statusMap(docVer?.aadhaar?.status), remarks: docVer?.aadhaar?.remarks || '' },
      pan: { status: statusMap(docVer?.pan?.status), remarks: docVer?.pan?.remarks || '' },
      gst: { status: statusMap(docVer?.gst?.status), remarks: docVer?.gst?.remarks || '' },
      fssai: { status: statusMap(docVer?.fssai?.status), remarks: docVer?.fssai?.remarks || '' },
      bank: { status: isBankVerified ? 'verified' : 'pending', remarks: user?.remarks_bankinfo || '' },
      personal: { status: isPersonalVerified ? 'verified' : 'pending', remarks: user?.remarks_personalinfo || '' }
    };
  };

  const fetchVerificationStatus = useCallback(async () => {
    const id = restaurantId || paramId;
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const user = loadDataFromStorage();
      
      if (user) {
        const personalStatusMap = { 0: 'pending', 1: 'verified', 2: 'rejected' };
        const personalStatus = personalStatusMap[Number(user.is_verify_personalinfo)] || 'pending';
        
        setPersonalInfo({
          status: personalStatus,
          remarks: user.remarks_personalinfo || '',
          ownerName: user.owner_name || '',
          restaurantName: user.organisation_name || user.restaurant_name || '',
          phone: user.phone || user.contact || user.owner_mobile || '',
          email: user.email || '',
          address: user.address || '',
          city: user.city || '',
          state: user.state || '',
          postal: user.postal || ''
        });

        const docStatus = parseDocVerification(user);
        setDocs(prev => {
          const newDocs = { ...prev };
          Object.keys(newDocs).forEach(key => {
            if (docStatus[key]) {
              newDocs[key] = { ...newDocs[key], status: docStatus[key].status, remarks: docStatus[key].remarks || '' };
            }
          });
          const isBankVerified = user.is_verify_bankinfo === 1 || user.is_verify_bankinfo === '1';
          newDocs.bank = { ...newDocs.bank, status: isBankVerified ? 'verified' : 'pending', remarks: user.remarks_bankinfo || '' };
          return newDocs;
        });
        setIsApproved(user.is_approved === 1 || user.is_approved === '1');
      }

      try {
        const response = await getData(`vandersuperadmin/restaurant/verification-status/${id}`);
        if (response.success) {
          const data = response.data;
          const personalStatusMap = { 0: 'pending', 1: 'verified', 2: 'rejected' };
          const personalStatus = personalStatusMap[Number(data.is_verify_personalinfo)] || 'pending';
          setPersonalInfo(prev => ({ ...prev, status: personalStatus, remarks: data.remarks_personalinfo || prev.remarks }));

          let docVer = {};
          try {
            docVer = data.doc_verification || {};
            if (typeof docVer === 'string') docVer = JSON.parse(docVer);
          } catch (e) { docVer = {}; }
          const docStatusMap = { 0: 'pending', 1: 'verified', 2: 'rejected' };

          setDocs(prev => {
            const newDocs = { ...prev };
            const isBankVerified = data.is_verify_bankinfo == 1 || data.is_verify_bankinfo === true;
            newDocs.bank = { ...newDocs.bank, status: isBankVerified ? 'verified' : 'pending', remarks: data.remarks_bankinfo || '' };

            ['aadhaar', 'pan', 'gst', 'fssai'].forEach(doc => {
              const docData = docVer[doc] || {};
              const statusCode = docData.status !== undefined ? docData.status : 0;
              newDocs[doc] = { ...newDocs[doc], status: docStatusMap[statusCode] || 'pending', remarks: docData.remarks || '' };
            });
            return newDocs;
          });
          setIsApproved(data.is_approved === true);
        }
      } catch (apiError) {
        console.log('API fetch failed, using localStorage data only');
      }
    } catch (error) {
      console.error('Fetch status error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurantId, paramId, loadDataFromStorage]);

  const fetchDocuments = useCallback(async () => {
    const id = restaurantId || paramId;
    if (!id) return;
    
    try {
      setLoading(true);
      const user = loadDataFromStorage();
      if (user) {
        setDocs(prev => ({
          ...prev,
          aadhaar: {
            ...prev.aadhaar,
            number: user.aadhar_number || '',
            frontImage: user.aadhar_front ? getImageUrl(user.aadhar_front) : null,
            backImage: user.aadhar_back ? getImageUrl(user.aadhar_back) : null,
          },
          pan: {
            ...prev.pan,
            number: user.pan_number || '',
            image: user.pan_card ? getImageUrl(user.pan_card) : null,
          },
          bank: {
            ...prev.bank,
            accountNumber: user.account_number || '',
            ifsc: user.ifsc || '',
            bankName: user.bank_name || '',
            accountHolder: user.account_holder || user.bank_account_holder || '',
            passbookImage: user.bank_pass ? getImageUrl(user.bank_pass) : null,
            cancelChequeImage: user.cancel_cheque ? getImageUrl(user.cancel_cheque) : null,
          },
          gst: {
            ...prev.gst,
            number: user.gst_number || '',
            image: user.gst_pic ? getImageUrl(user.gst_pic) : null,
          },
          fssai: {
            ...prev.fssai,
            number: user.food_license_number || '',
            image: user.food_license_pic ? getImageUrl(user.food_license_pic) : null,
          },
        }));
      }

      try {
        const response = await getData(`vandersuperadmin/restaurant/documents/${id}`);
        if (response.success) {
          const data = response.data;
          setDocs(prev => ({
            ...prev,
            aadhaar: {
              ...prev.aadhaar,
              number: data.aadhar_number || prev.aadhaar.number,
              frontImage: data.aadhar_front ? getImageUrl(data.aadhar_front) : prev.aadhaar.frontImage,
              backImage: data.aadhar_back ? getImageUrl(data.aadhar_back) : prev.aadhaar.backImage,
            },
            pan: {
              ...prev.pan,
              number: data.pan_number || prev.pan.number,
              image: data.pan_card ? getImageUrl(data.pan_card) : prev.pan.image,
            },
            bank: {
              ...prev.bank,
              accountNumber: data.account_number || prev.bank.accountNumber,
              ifsc: data.ifsc || prev.bank.ifsc,
              bankName: data.bank_name || prev.bank.bankName,
              accountHolder: data.bank_account_holder || prev.bank.accountHolder,
              passbookImage: data.bank_pass ? getImageUrl(data.bank_pass) : prev.bank.passbookImage,
              cancelChequeImage: data.cancel_cheque ? getImageUrl(data.cancel_cheque) : prev.bank.cancelChequeImage,
            },
            gst: {
              ...prev.gst,
              number: data.gst_number || prev.gst.number,
              image: data.gst_pic ? getImageUrl(data.gst_pic) : prev.gst.image,
            },
            fssai: {
              ...prev.fssai,
              number: data.fssai_number || prev.fssai.number,
              image: data.food_license_pic ? getImageUrl(data.food_license_pic) : prev.fssai.image,
            },
          }));
        }
      } catch (apiError) {
        console.log('API fetch failed, using localStorage data only');
      }
      await fetchVerificationStatus();
    } catch (error) {
      console.error('Fetch documents error:', error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, paramId, loadDataFromStorage, fetchVerificationStatus, getImageUrl]);

  useEffect(() => {
    const id = restaurantId || paramId;
    if (id) {
      fetchDocuments();
    } else {
      const user = loadDataFromStorage();
      if (user) {
        const id = user.id || user.restaurantId;
        if (id) {
          setRestaurantId(id);
          fetchDocuments();
        }
      } else {
        setLoading(false);
      }
    }
  }, [restaurantId, paramId, fetchDocuments, loadDataFromStorage]);

  const isFullyVerified = () => {
    return personalInfo.status === 'verified' &&
           docs.aadhaar.status === 'verified' &&
           docs.pan.status === 'verified' &&
           docs.gst.status === 'verified' &&
           docs.fssai.status === 'verified' &&
           docs.bank.status === 'verified' &&
           isApproved === true;
  };

  const getStatusMeta = (status) => {
    switch (status) {
      case 'verified': return { label: 'Verified', color: '#16a34a' };
      case 'review': return { label: 'Under Review', color: '#f59e0b' };
      case 'rejected': return { label: 'Rejected', color: '#dc2626' };
      case 'pending': return { label: 'Pending', color: '#f97316' };
      default: return { label: 'Not Submitted', color: '#6b7280' };
    }
  };

  const getCompletionPercent = () => {
    const items = [{ status: personalInfo.status }, ...Object.values(docs).map(doc => ({ status: doc.status }))];
    const completed = items.filter(item => item.status === 'verified' || item.status === 'review').length;
    return Math.round((completed / items.length) * 100);
  };

  const updateDocField = useCallback((docKey, field, value) => {
    setDocs(prev => ({ ...prev, [docKey]: { ...prev[docKey], [field]: value } }));
  }, []);

  const handleTextChange = (docKey, field, value) => {
    updateDocField(docKey, field, value);
  };

  const handleSubmitDoc = (docKey) => {
    updateDocField(docKey, 'status', 'review');
    setExpandedDoc(null);
    alert(`${docs[docKey].name} has been submitted for review.`);
  };

  const submitAll = async () => {
    setSubmitting(true);
    try {
      const id = restaurantId || paramId;
      if (!id) {
        alert('Restaurant ID not found');
        setSubmitting(false);
        return;
      }

      const payload = {
        aadhaar: { number: docs.aadhaar.number, frontImage: docs.aadhaar.frontImage || null, backImage: docs.aadhaar.backImage || null },
        pan: { number: docs.pan.number, image: docs.pan.image || null },
        bank: { accountNumber: docs.bank.accountNumber, accountHolder: docs.bank.accountHolder, ifsc: docs.bank.ifsc, bankName: docs.bank.bankName, passbookImage: docs.bank.passbookImage || null, cancelChequeImage: docs.bank.cancelChequeImage || null },
        gst: { number: docs.gst.number, image: docs.gst.image || null },
        fssai: { number: docs.fssai.number, image: docs.fssai.image || null }
      };

      Object.keys(payload).forEach(key => {
        Object.keys(payload[key]).forEach(field => {
          if (payload[key][field] === null || payload[key][field] === undefined) delete payload[key][field];
        });
      });

      const response = await putData(`vandersuperadmin/restaurant/documents/${id}`, payload);
      if (response.success) {
        alert('Documents updated successfully!');
        await fetchDocuments();
        await fetchVerificationStatus();
      } else {
        alert('Error: ' + (response.message || 'Update failed.'));
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = (docKey, field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          updateDocField(docKey, field, event.target.result);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  // 🎨 Dynamic Styles based on Orange Theme
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      padding: '24px',
      transition: 'background-color 0.3s ease, color 0.3s ease',
    },
    maxWidth: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    header: {
      background: theme.primaryGradient,
      borderRadius: '20px',
      padding: '24px 32px',
      marginBottom: '24px',
      position: 'relative',
      overflow: 'hidden',
    },
    headerOverlay: {
      position: 'absolute',
      top: '-50%',
      right: '-10%',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.05)',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      position: 'relative',
      zIndex: 1,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#ffffff',
      background: 'rgba(255,255,255,0.15)',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      transition: 'all 0.3s ease',
    },
    headerTitle: {
      fontSize: '20px',
      fontWeight: 700,
      color: '#ffffff',
    },
    progressCard: {
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '20px',
      padding: '20px',
      border: '1px solid rgba(255,255,255,0.2)',
      position: 'relative',
      zIndex: 1,
    },
    progressInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
    },
    progLabel: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1px',
    },
    progStatus: {
      color: '#ffffff',
      fontSize: '20px',
      fontWeight: 700,
    },
    percentCircle: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid #ffffff',
    },
    percentText: {
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: 700,
    },
    progressBarBg: {
      height: '6px',
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '10px',
      overflow: 'hidden',
    },
    progressBarFill: (percent) => ({
      height: '100%',
      width: `${percent}%`,
      background: '#ffffff',
      borderRadius: '10px',
      transition: 'width 0.5s ease',
    }),
    content: {
      marginTop: '24px',
    },
    infoAlert: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      borderRadius: '16px',
      background: isFullyVerified() ? theme.successBg : theme.warningBg,
      border: `1px solid ${isFullyVerified() ? theme.successBorder : theme.warningBorder}`,
      marginBottom: '24px',
      transition: 'all 0.3s ease',
    },
    infoText: {
      flex: 1,
      fontSize: '13px',
      color: isFullyVerified() ? (isDark ? '#4ade80' : '#15803d') : (isDark ? '#fbbf24' : '#d97706'),
      fontWeight: 500,
    },
    sectionHeading: {
      fontSize: '12px',
      fontWeight: 700,
      color: theme.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '12px',
      marginTop: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    docCard: (status) => {
      const meta = getStatusMeta(status);
      return {
        background: theme.cardBg,
        borderRadius: '16px',
        marginBottom: '12px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : meta.color + '30'}`,
        overflow: 'hidden',
        boxShadow: theme.shadow,
        transition: 'all 0.3s ease',
      };
    },
    docHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      cursor: 'pointer',
    },
    iconContainer: (status) => {
      const meta = getStatusMeta(status);
      return {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark ? `${meta.color}20` : `${meta.color}15`,
        color: meta.color,
        fontSize: '20px',
        flexShrink: 0,
        position: 'relative',
      };
    },
    docInfo: {
      flex: 1,
      marginLeft: '14px',
    },
    docName: {
      fontSize: '15px',
      fontWeight: 600,
      color: theme.text,
    },
    requiredStar: {
      color: '#ef4444',
    },
    statusLabel: (status) => {
      const meta = getStatusMeta(status);
      return {
        fontSize: '12px',
        fontWeight: 600,
        color: meta.color,
      };
    },
    expandIcon: {
      color: theme.textSecondary,
      fontSize: '18px',
    },
    docForm: {
      padding: '16px',
      borderTop: `1px solid ${theme.border}`,
      background: isDark ? 'rgba(0,0,0,0.2)' : '#fff7ed',
      transition: 'all 0.3s ease',
    },
    inputGroup: {
      marginBottom: '14px',
    },
    label: {
      fontSize: '12px',
      fontWeight: 600,
      color: theme.textSecondary,
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      padding: '10px 14px',
      borderRadius: '8px',
      border: `1.5px solid ${theme.border}`,
      fontSize: '14px',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      background: theme.inputBg,
      color: theme.text,
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    uploadBox: {
      height: '120px',
      border: `2px dashed ${theme.border}`,
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: isDark ? 'rgba(255,255,255,0.03)' : '#ffedd5',
      position: 'relative',
      overflow: 'hidden',
    },
    uploadBoxImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      padding: '4px',
    },
    uploadPlaceholder: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      color: theme.textSecondary,
    },
    uploadLabel: {
      fontSize: '12px',
      fontWeight: 500,
    },
    saveBtn: {
      width: '100%',
      padding: '12px',
      borderRadius: '10px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '8px',
    },
    remarksContainer: {
      padding: '10px',
      background: theme.warningBg,
      borderRadius: '8px',
      borderLeft: '3px solid #f59e0b',
      marginBottom: '12px',
      transition: 'all 0.3s ease',
    },
    remarksLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: theme.textSecondary,
    },
    remarksText: {
      fontSize: '13px',
      color: theme.text,
      marginTop: '4px',
    },
    submitAllBtn: {
      width: '100%',
      padding: '16px',
      borderRadius: '14px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '20px',
    },
    submitAllBtnDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    refreshBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      background: theme.cardBg,
      cursor: 'pointer',
      marginTop: '12px',
      width: '100%',
      fontSize: '14px',
      fontWeight: 600,
      color: theme.primary,
      transition: 'all 0.3s ease',
    },
    verifiedContainer: {
      marginTop: '20px',
      textAlign: 'center',
      padding: '24px',
      background: theme.cardBg,
      borderRadius: '16px',
      border: `1px solid ${theme.successBorder}`,
      transition: 'all 0.3s ease',
    },
    verifiedText: {
      fontSize: '18px',
      fontWeight: 700,
      color: isDark ? '#4ade80' : '#16a34a',
      marginBottom: '16px',
    },
    goBackBtn: {
      width: '100%',
      padding: '14px',
      borderRadius: '12px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    footerInfo: {
      textAlign: 'center',
      color: theme.textSecondary,
      fontSize: '12px',
      marginTop: '24px',
      fontWeight: 500,
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: '16px',
      backgroundColor: theme.bg,
      transition: 'background-color 0.3s ease',
    },
    loadingSpinner: {
      animation: 'spin 1s linear infinite',
      fontSize: '40px',
      color: theme.primary,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingContainer}>
          <FaSpinner style={styles.loadingSpinner} />
          <div style={{ color: theme.textSecondary }}>Loading verification status...</div>
        </div>
      </div>
    );
  }

  const percent = getCompletionPercent();
  const fullyVerified = isFullyVerified();

  const DocumentCard = ({ docKey, data }) => {
    const isExpanded = expandedDoc === docKey;
    const meta = getStatusMeta(data.status);
    const isRejected = data.status === 'rejected';

    // Helper to render image with error handling
    const renderImage = (imageUrl, altText) => {
      if (!imageUrl) {
        return (
          <div style={styles.uploadPlaceholder}>
            <FaCamera size={24} />
            <span style={styles.uploadLabel}>Upload Image</span>
          </div>
        );
      }
      return (
        <img 
          src={imageUrl} 
          alt={altText} 
          style={styles.uploadBoxImage}
          onError={(e) => {
            console.error(`Failed to load image: ${imageUrl}`);
            e.target.style.display = 'none';
            // Show placeholder on error
            const parent = e.target.parentNode;
            const placeholder = document.createElement('div');
            placeholder.style.cssText = `
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 6px;
              color: ${theme.textSecondary};
              width: 100%;
              height: 100%;
            `;
            placeholder.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <span style="font-size: 12px; font-weight: 500;">Image not available</span>
            `;
            parent.appendChild(placeholder);
          }}
        />
      );
    };

    return (
      <div style={styles.docCard(data.status)}>
        <div style={styles.docHeader} onClick={() => setExpandedDoc(isExpanded ? null : docKey)}>
          <div style={styles.iconContainer(data.status)}>
            {data.icon}
            {data.status === 'verified' && (
              <div style={{ position: 'absolute', bottom: -4, right: -4, background: theme.cardBg, borderRadius: '50%', padding: 2, display: 'flex', border: `1px solid ${theme.border}` }}>
                <FaCheckCircle size={16} color="#16a34a" />
              </div>
            )}
          </div>
          <div style={styles.docInfo}>
            <div style={styles.docName}>
              {data.name} {data.required && <span style={styles.requiredStar}>*</span>}
            </div>
            <div style={styles.statusLabel(data.status)}>{meta.label}</div>
          </div>
          <div style={styles.expandIcon}>
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>

        {isExpanded && (
          <div style={styles.docForm}>
            {data.remarks && (
              <div style={styles.remarksContainer}>
                <div style={styles.remarksLabel}>Admin Remarks:</div>
                <div style={styles.remarksText}>{data.remarks}</div>
              </div>
            )}

            {docKey === 'aadhaar' && (
              <>
                <div style={styles.inputGroup}>
                  <label style={styles.label}><FaIdCard style={{marginRight: 6}} /> Aadhaar Number</label>
                  <input type="text" style={styles.input} placeholder="0000 0000 0000" value={data.number || ''} onChange={(e) => handleTextChange(docKey, 'number', e.target.value)} disabled={data.status === 'verified'} />
                </div>
                <div style={styles.row}>
                  <div>
                    <label style={styles.label}>Front Side</label>
                    <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'frontImage')}>
                      {renderImage(data.frontImage, 'Aadhaar Front')}
                    </div>
                  </div>
                  <div>
                    <label style={styles.label}>Back Side</label>
                    <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'backImage')}>
                      {renderImage(data.backImage, 'Aadhaar Back')}
                    </div>
                  </div>
                </div>
              </>
            )}

            {docKey === 'pan' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}><FaCreditCard style={{marginRight: 6}} /> PAN Number</label>
                <input type="text" style={styles.input} placeholder="ABCDE1234F" value={data.number || ''} onChange={(e) => handleTextChange(docKey, 'number', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}>PAN Card Image</label>
                <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'image')}>
                  {renderImage(data.image, 'PAN Card')}
                </div>
              </div>
            )}

            {docKey === 'bank' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}><FaUser style={{marginRight: 6}} /> Account Holder Name</label>
                <input type="text" style={styles.input} placeholder="Account holder name" value={data.accountHolder || ''} onChange={(e) => handleTextChange(docKey, 'accountHolder', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}><FaCreditCard style={{marginRight: 6}} /> Account Number</label>
                <input type="text" style={styles.input} placeholder="Account number" value={data.accountNumber || ''} onChange={(e) => handleTextChange(docKey, 'accountNumber', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}>IFSC Code</label>
                <input type="text" style={styles.input} placeholder="IFSC code" value={data.ifsc || ''} onChange={(e) => handleTextChange(docKey, 'ifsc', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}><FaBuilding style={{marginRight: 6}} /> Bank Name</label>
                <input type="text" style={styles.input} placeholder="Bank name" value={data.bankName || ''} onChange={(e) => handleTextChange(docKey, 'bankName', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}>Passbook Image</label>
                <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'passbookImage')}>
                  {renderImage(data.passbookImage, 'Passbook')}
                </div>
                <label style={{ ...styles.label, marginTop: '12px' }}>Cancel Cheque Image</label>
                <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'cancelChequeImage')}>
                  {renderImage(data.cancelChequeImage, 'Cancel Cheque')}
                </div>
              </div>
            )}

            {docKey === 'gst' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}><FaFileInvoice style={{marginRight: 6}} /> GST Number</label>
                <input type="text" style={styles.input} placeholder="GST number" value={data.number || ''} onChange={(e) => handleTextChange(docKey, 'number', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}>GST Certificate</label>
                <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'image')}>
                  {renderImage(data.image, 'GST Certificate')}
                </div>
              </div>
            )}

            {docKey === 'fssai' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}><FaCertificate style={{marginRight: 6}} /> FSSAI License Number</label>
                <input type="text" style={styles.input} placeholder="License number" value={data.number || ''} onChange={(e) => handleTextChange(docKey, 'number', e.target.value)} disabled={data.status === 'verified'} />
                <label style={{ ...styles.label, marginTop: '12px' }}>FSSAI License</label>
                <div style={styles.uploadBox} onClick={() => data.status !== 'verified' && handleImageUpload(docKey, 'image')}>
                  {renderImage(data.image, 'FSSAI License')}
                </div>
              </div>
            )}

            {data.status !== 'verified' && (
              <button style={styles.saveBtn} onClick={() => handleSubmitDoc(docKey)}>
                {isRejected ? 'Re-submit' : 'Submit'} {data.name}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .back-btn:hover { background: rgba(255,255,255,0.25) !important; transform: translateX(-2px); }
        .upload-box:hover { border-color: #f97316 !important; background: ${isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5'} !important; }
        .submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(249, 115, 22, 0.4); }
        input:focus:not(:disabled) { outline: none; border-color: #f97316 !important; box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.15) !important; }
      `}</style>

      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerOverlay} />
          <div style={styles.headerTop}>
            <button className="back-btn" style={styles.backButton} onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back
            </button>
            <span style={styles.headerTitle}>{editMode ? 'Verification Status' : 'Verification Center'}</span>
            <div style={{ width: '40px' }} />
          </div>

          {/* Progress Card */}
          <div style={styles.progressCard}>
            <div style={styles.progressInfo}>
              <div>
                <div style={styles.progLabel}>PROFILE COMPLETION</div>
                <div style={styles.progStatus}>{fullyVerified ? 'Complete ✅' : 'In Progress'}</div>
              </div>
              <div style={styles.percentCircle}>
                <span style={styles.percentText}>{percent}%</span>
              </div>
            </div>
            <div style={styles.progressBarBg}>
              <div style={styles.progressBarFill(percent)} />
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Info Alert */}
          <div style={styles.infoAlert}>
            <FaInfoCircle color={fullyVerified ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#fbbf24' : '#f97316')} size={18} />
            <span style={styles.infoText}>
              {fullyVerified ? '🎉 All your details are verified. Your store is ready to go!' : 'Submit documents for verification. Once approved, your store will be active.'}
            </span>
          </div>

          {/* Personal Info Section */}
          <div style={styles.sectionHeading}><FaUser /> Personal Information</div>
          <div style={styles.docCard(personalInfo.status)}>
            <div style={styles.docHeader}>
              <div style={styles.iconContainer(personalInfo.status)}>
                <FaUser />
                {personalInfo.status === 'verified' && (
                  <div style={{ position: 'absolute', bottom: -4, right: -4, background: theme.cardBg, borderRadius: '50%', padding: 2, display: 'flex', border: `1px solid ${theme.border}` }}>
                    <FaCheckCircle size={16} color="#16a34a" />
                  </div>
                )}
              </div>
              <div style={styles.docInfo}>
                <div style={styles.docName}>Personal Information</div>
                <div style={styles.statusLabel(personalInfo.status)}>{getStatusMeta(personalInfo.status).label}</div>
              </div>
            </div>
            <div style={styles.docForm}>
              <div style={styles.inputGroup}>
                <label style={styles.label}><FaUser style={{marginRight: 6}} /> Owner Name</label>
                <div style={{ ...styles.input, background: theme.hoverBg, cursor: 'default', color: theme.text }}>
                  {personalInfo.ownerName || 'Not provided'}
                </div>
                
                <label style={{ ...styles.label, marginTop: '12px' }}><FaUtensils style={{marginRight: 6}} /> Restaurant Name</label>
                <div style={{ ...styles.input, background: theme.hoverBg, cursor: 'default', color: theme.text }}>
                  {personalInfo.restaurantName || 'Not provided'}
                </div>
                
                <label style={{ ...styles.label, marginTop: '12px' }}><FaPhone style={{marginRight: 6}} /> Phone</label>
                <div style={{ ...styles.input, background: theme.hoverBg, cursor: 'default', color: theme.text }}>
                  {personalInfo.phone || 'Not provided'}
                </div>
                
                <label style={{ ...styles.label, marginTop: '12px' }}><FaEnvelope style={{marginRight: 6}} /> Email</label>
                <div style={{ ...styles.input, background: theme.hoverBg, cursor: 'default', color: theme.text }}>
                  {personalInfo.email || 'Not provided'}
                </div>
                
                <label style={{ ...styles.label, marginTop: '12px' }}><FaMapMarkerAlt style={{marginRight: 6}} /> Address</label>
                <div style={{ ...styles.input, background: theme.hoverBg, cursor: 'default', color: theme.text }}>
                  {personalInfo.address || 'Not provided'}
                </div>
                
                {personalInfo.remarks && (
                  <div style={styles.remarksContainer}>
                    <div style={styles.remarksLabel}>Admin Remarks:</div>
                    <div style={styles.remarksText}>{personalInfo.remarks}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Documents Section */}
          <div style={styles.sectionHeading}><FaIdCard /> Documents</div>
          {Object.entries(docs).map(([key, data]) => (
            <DocumentCard key={key} docKey={key} data={data} />
          ))}

          {/* Action Buttons */}
          {!fullyVerified ? (
            <>
              <button
                className="submit-btn"
                style={{ ...styles.submitAllBtn, ...(submitting ? styles.submitAllBtnDisabled : {}) }}
                onClick={submitAll}
                disabled={submitting}
              >
                {submitting ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaShieldAlt />}
                {submitting ? 'SAVING...' : (editMode ? 'UPDATE DOCUMENTS' : 'SUBMIT ALL FOR REVIEW')}
              </button>

              <button style={styles.refreshBtn} onClick={() => {
                setRefreshing(true);
                fetchDocuments();
              }}>
                <FaSync className={refreshing ? 'spin' : ''} /> Refresh Status
              </button>
            </>
          ) : (
            <div style={styles.verifiedContainer}>
              <div style={styles.verifiedText}>✅ All documents verified</div>
              <button style={styles.goBackBtn} onClick={() => navigate(-1)}>
                <FaArrowLeft style={{marginRight: 8}} /> Back to Profile
              </button>
            </div>
          )}

          <div style={styles.footerInfo}>
            Need help? Contact Merchant Support
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationScreen;